// AP Statistics Curriculum - Units 4-6
import { Unit } from '../components/GradeLessonPage';

export const unit4: Unit = {
  id: 'unit4',
  title: 'Unit 4: Probability, Random Variables, and Probability Distributions',
  description: 'Probability rules, conditional probability, and normal/binomial distributions',
  weight: '10%–20%',
  topics: [
    {
      id: 'probability-basics',
      title: 'Basic Probability',
      description: 'Probability rules, complementary events, addition and multiplication rules',
      skills: [
        {
          id: 'probability-rules',
          name: 'Basic Probability Rules',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `prob-${i + 1}`,
            question: i === 0 ? 'A bag contains 5 red, 3 blue, and 2 green marbles. What is P(red)? What is P(not red)?' :
                     i === 1 ? 'P(A) = 0.40, P(B) = 0.35, P(A and B) = 0.15. Calculate P(A or B).' :
                     i === 2 ? 'If P(E) = 0.25, what is P(not E)?' :
                     `Calculate probabilities using ${['complement rule', 'addition rule', 'sample space'][i % 3]}.`,
            answer: i === 0 ? 'Total marbles = 5+3+2 = 10\nP(red) = 5/10 = 0.50\nP(not red) = 1 - P(red) = 1 - 0.50 = 0.50 (or count: (3+2)/10 = 0.50)' :
                   i === 1 ? 'Addition Rule: P(A or B) = P(A) + P(B) - P(A and B)\nP(A or B) = 0.40 + 0.35 - 0.15 = 0.60' :
                   i === 2 ? 'Complement Rule: P(not E) = 1 - P(E) = 1 - 0.25 = 0.75' :
                   'Solution using appropriate probability rules.',
            steps: [
              'Complement: P(not A) = 1 - P(A)',
              'Addition (general): P(A or B) = P(A) + P(B) - P(A and B)',
              'Addition (disjoint): P(A or B) = P(A) + P(B) if A and B can\'t occur together',
              'All probabilities between 0 and 1',
              'Sum of all outcomes = 1'
            ]
          }))
        },
        {
          id: 'conditional-probability',
          name: 'Conditional Probability',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `cond-${i + 1}`,
            question: i === 0 ? 'Of 100 students, 60 play sports and 40 don\'t. Of those who play sports, 45 passed the exam. Of those who don\'t, 25 passed. What is P(pass | plays sports)?' :
                     i === 1 ? 'P(A) = 0.50, P(B) = 0.40, P(A and B) = 0.20. Calculate P(A | B).' :
                     i === 2 ? 'Are events A and B independent if P(A) = 0.6, P(B) = 0.5, and P(A | B) = 0.6?' :
                     `Calculate conditional probability P(${['A', 'B'][i % 2]} | ${['B', 'A'][i % 2]}) from given information.`,
            answer: i === 0 ? 'P(pass | plays sports) = (number who play sports and passed) / (number who play sports)\n= 45/60 = 0.75 or 75%' :
                   i === 1 ? 'Conditional Probability: P(A | B) = P(A and B) / P(B)\nP(A | B) = 0.20 / 0.40 = 0.50' :
                   i === 2 ? 'Yes, they are independent. P(A | B) = 0.6 = P(A), so knowing B occurred doesn\'t change probability of A. Also can check: P(A and B) = P(A)×P(B) = 0.6×0.5 = 0.30.' :
                   'Solution with conditional probability calculations.',
            steps: [
              'Formula: P(A | B) = P(A and B) / P(B)',
              'Read as "probability of A given B has occurred"',
              'B becomes the new sample space',
              'Independence: P(A | B) = P(A) or P(A and B) = P(A)×P(B)',
              'Use two-way tables or tree diagrams for complex problems'
            ]
          }))
        },
        {
          id: 'independence',
          name: 'Independent vs Disjoint Events',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `indep-${i + 1}`,
            question: i === 0 ? 'Explain the difference between independent events and disjoint (mutually exclusive) events.' :
                     i === 1 ? 'Can two events be both independent and disjoint? Explain.' :
                     i === 2 ? 'Roll a die twice. Are the outcomes independent? Are they disjoint?' :
                     `Determine if events are ${['independent', 'disjoint', 'both or neither'][i % 3]}.`,
            answer: i === 0 ? 'Disjoint: Events cannot occur together, P(A and B) = 0. Knowing A occurred means B cannot occur.\nIndependent: Occurrence of one doesn\'t affect probability of other, P(A | B) = P(A). Events CAN occur together.' :
                   i === 1 ? 'No, except trivially when P(A) or P(B) = 0. If events are disjoint (P(A and B) = 0) and both have positive probability, then P(A | B) = 0 ≠ P(A), so they are dependent, not independent.' :
                   i === 2 ? 'Independent: Yes, the outcome of the second roll doesn\'t depend on the first roll.\nDisjoint: No, both outcomes occur (you get a number each time). The events are separate trials, not mutually exclusive outcomes.' :
                   'Solution explaining independence and disjoint concepts.',
            steps: [
              'Disjoint: P(A and B) = 0, events can\'t both happen',
              'Independent: P(A | B) = P(A), knowing one doesn\'t change other',
              'Disjoint events with P > 0 are dependent',
              'Independent events CAN occur together',
              'Don\'t confuse these concepts!'
            ]
          }))
        }
      ]
    },
    {
      id: 'random-variables',
      title: 'Random Variables',
      description: 'Discrete random variables, expected value, variance',
      skills: [
        {
          id: 'expected-value',
          name: 'Expected Value and Variance',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `ev-${i + 1}`,
            question: i === 0 ? 'Random variable X: P(X=1)=0.2, P(X=2)=0.5, P(X=3)=0.3. Calculate E(X) and Var(X).' :
                     i === 1 ? 'Game costs $5 to play. You win $20 with probability 0.1, $10 with probability 0.2, $0 with probability 0.7. What is expected profit?' :
                     i === 2 ? 'If E(X) = 10 and Var(X) = 4, find E(3X + 5) and Var(3X + 5).' :
                     `Calculate expected value and variance for random variable with ${Math.floor(Math.random() * 3) + 3} outcomes.`,
            answer: i === 0 ? 'E(X) = 1(0.2) + 2(0.5) + 3(0.3) = 0.2 + 1.0 + 0.9 = 2.1\nE(X²) = 1²(0.2) + 2²(0.5) + 3²(0.3) = 0.2 + 2.0 + 2.7 = 4.9\nVar(X) = E(X²) - [E(X)]² = 4.9 - (2.1)² = 4.9 - 4.41 = 0.49\nSD(X) = √0.49 = 0.7' :
                   i === 1 ? 'Winnings: E(winnings) = 20(0.1) + 10(0.2) + 0(0.7) = 2 + 2 + 0 = $4\nCost = $5\nExpected profit = $4 - $5 = -$1\nExpect to lose $1 per game on average.' :
                   i === 2 ? 'E(3X + 5) = 3E(X) + 5 = 3(10) + 5 = 35\nVar(3X + 5) = 3²Var(X) = 9(4) = 36\nNote: Adding constant doesn\'t affect variance.' :
                   'Solution with expected value and variance calculations.',
            steps: [
              'Expected value: E(X) = Σ[x·P(x)]',
              'Variance: Var(X) = E(X²) - [E(X)]²',
              'Or: Var(X) = Σ[(x - μ)²·P(x)]',
              'E(aX + b) = aE(X) + b',
              'Var(aX + b) = a²Var(X)',
              'SD(X) = √Var(X)'
            ]
          }))
        },
        {
          id: 'combining-variables',
          name: 'Combining Random Variables',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `combine-${i + 1}`,
            question: i === 0 ? 'X and Y are independent. E(X)=5, E(Y)=3, Var(X)=2, Var(Y)=4. Find E(X+Y) and Var(X+Y).' :
                     i === 1 ? 'Why does Var(X+Y) = Var(X) + Var(Y) only when X and Y are independent?' :
                     i === 2 ? 'Store A averages 50 customers/day (SD=8). Store B averages 40 customers/day (SD=6). Total customers if independent?' :
                     `Combine random variables using ${['addition', 'subtraction', 'linear transformation'][i % 3]}.`,
            answer: i === 0 ? 'E(X+Y) = E(X) + E(Y) = 5 + 3 = 8 (always true)\nVar(X+Y) = Var(X) + Var(Y) = 2 + 4 = 6 (true because independent)\nSD(X+Y) = √6 ≈ 2.45' :
                   i === 1 ? 'Variance measures spread. When independent, variations in X and Y don\'t affect each other, so total variation is sum of individual variations. When dependent, need covariance term: Var(X+Y) = Var(X) + Var(Y) + 2Cov(X,Y).' :
                   i === 2 ? 'E(Total) = E(A) + E(B) = 50 + 40 = 90 customers\nAssuming independence:\nVar(Total) = Var(A) + Var(B) = 8² + 6² = 64 + 36 = 100\nSD(Total) = √100 = 10 customers' :
                   'Solution for combining random variables.',
            steps: [
              'E(X ± Y) = E(X) ± E(Y) always',
              'Var(X + Y) = Var(X) + Var(Y) if independent',
              'Var(X - Y) = Var(X) + Var(Y) if independent',
              'For independent X,Y: variances add for both + and -',
              'SD(X + Y) = √[Var(X) + Var(Y)] when independent'
            ]
          }))
        }
      ]
    },
    {
      id: 'distributions',
      title: 'Probability Distributions',
      description: 'Normal, binomial, and geometric distributions',
      skills: [
        {
          id: 'normal-distribution',
          name: 'Normal Distribution and z-scores',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `normal-${i + 1}`,
            question: i === 0 ? 'Heights are normally distributed with mean 170 cm and SD 10 cm. Find the z-score for 185 cm and interpret.' :
                     i === 1 ? 'For N(100, 15), what percentage of values fall between 85 and 115? Use empirical rule.' :
                     i === 2 ? 'Test scores: N(75, 8). What score is at the 90th percentile? (z = 1.28 for 90th percentile)' :
                     `Solve normal distribution problem with μ=${Math.floor(Math.random() * 50) + 50}, σ=${Math.floor(Math.random() * 10) + 5}.`,
            answer: i === 0 ? 'z = (x - μ) / σ = (185 - 170) / 10 = 15/10 = 1.5\nInterpretation: 185 cm is 1.5 standard deviations above the mean height.' :
                   i === 1 ? 'μ ± σ means 85 to 115 (one SD from mean).\nEmpirical Rule: ~68% of data falls within 1 SD of mean.\nSo approximately 68% of values fall between 85 and 115.' :
                   i === 2 ? 'x = μ + z·σ = 75 + 1.28(8) = 75 + 10.24 = 85.24\nA score of approximately 85.2 is at the 90th percentile.' :
                   'Solution using normal distribution calculations.',
            steps: [
              'z-score: z = (x - μ) / σ',
              'Standardizes values for comparison',
              'Empirical Rule (68-95-99.7): ~68% within 1 SD, ~95% within 2 SD, ~99.7% within 3 SD',
              'Use table/calculator for probabilities',
              'Convert back: x = μ + z·σ'
            ]
          }))
        },
        {
          id: 'binomial-distribution',
          name: 'Binomial Distribution',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `binom-${i + 1}`,
            question: i === 0 ? 'Flip fair coin 10 times. What is probability of exactly 6 heads? Calculate mean and SD.' :
                     i === 1 ? 'Multiple choice test: 20 questions, 4 choices each. If guessing randomly, what\'s expected number correct?' :
                     i === 2 ? '80% of customers are satisfied. Survey 15 customers. P(at least 13 satisfied)?' :
                     `Solve binomial problem with n=${Math.floor(Math.random() * 15) + 5}, p=${(Math.random() * 0.5 + 0.3).toFixed(2)}.`,
            answer: i === 0 ? 'X ~ Binomial(n=10, p=0.5)\nP(X=6) = C(10,6)(0.5)⁶(0.5)⁴ = 210(0.5)¹⁰ = 210/1024 ≈ 0.205\nMean: μ = np = 10(0.5) = 5\nSD: σ = √[np(1-p)] = √[10(0.5)(0.5)] = √2.5 ≈ 1.58' :
                   i === 1 ? 'X ~ Binomial(n=20, p=0.25)\nE(X) = np = 20(0.25) = 5\nExpect to get 5 questions correct by random guessing.' :
                   i === 2 ? 'X ~ Binomial(n=15, p=0.80)\nP(X ≥ 13) = P(X=13) + P(X=14) + P(X=15)\nUse calculator: binomcdf(15, 0.80, 12) or 1 - binomcdf(15, 0.80, 12)\nP(X ≥ 13) ≈ 0.398' :
                   'Solution using binomial distribution.',
            steps: [
              'Requirements: Fixed n trials, two outcomes, constant p, independent trials',
              'P(X = k) = C(n,k)·p^k·(1-p)^(n-k)',
              'Mean: μ = np',
              'Standard deviation: σ = √[np(1-p)]',
              'Use calculator for probabilities'
            ]
          }))
        },
        {
          id: 'geometric-distribution',
          name: 'Geometric Distribution',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `geom-${i + 1}`,
            question: i === 0 ? 'Probability of success is 0.2. What is probability that first success occurs on 5th trial?' :
                     i === 1 ? 'Roll die until get a 6. What is expected number of rolls?' :
                     i === 2 ? 'Free throw shooter makes 70% of shots. P(first make is on 1st, 2nd, or 3rd shot)?' :
                     `Solve geometric distribution problem with p=${(Math.random() * 0.6 + 0.2).toFixed(2)}.`,
            answer: i === 0 ? 'X ~ Geometric(p=0.2)\nP(X=5) = (1-p)^(k-1)·p = (0.8)⁴(0.2) = 0.4096(0.2) = 0.08192\nAbout 8.2% chance first success is on 5th trial.' :
                   i === 1 ? 'X ~ Geometric(p=1/6)\nE(X) = 1/p = 1/(1/6) = 6\nExpect to roll 6 times on average to get first 6.' :
                   i === 2 ? 'X ~ Geometric(p=0.70)\nP(X ≤ 3) = P(X=1) + P(X=2) + P(X=3)\n= 0.70 + (0.30)(0.70) + (0.30)²(0.70)\n= 0.70 + 0.21 + 0.063 = 0.973' :
                   'Solution using geometric distribution.',
            steps: [
              'Geometric: trials until first success',
              'P(X = k) = (1-p)^(k-1)·p',
              'Mean: E(X) = 1/p',
              'Memoryless property: past trials don\'t affect future',
              'P(X ≤ k) = 1 - (1-p)^k'
            ]
          }))
        }
      ]
    }
  ]
};

export const unit5: Unit = {
  id: 'unit5',
  title: 'Unit 5: Sampling Distributions',
  description: 'Understanding variability, Central Limit Theorem, sampling distributions',
  weight: '7%–12%',
  topics: [
    {
      id: 'sampling-variability',
      title: 'Sampling Variability',
      description: 'Understanding sample-to-sample variation',
      skills: [
        {
          id: 'variability-concept',
          name: 'Understanding Sampling Variability',
          difficulty: 'Easy',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `var-${i + 1}`,
            question: i === 0 ? 'Population mean is 50. You take three samples of n=10 and get means: 48, 52, 49. Explain why they differ from 50.' :
                     i === 1 ? 'What is sampling variability and why does it occur?' :
                     i === 2 ? 'How does increasing sample size affect sampling variability?' :
                     `Explain sampling variability in context of ${['polls', 'quality control', 'medical studies'][i % 3]}.`,
            answer: i === 0 ? 'Sampling variability: Different samples from same population give different statistics due to random chance. Each sample of 10 captures slightly different individuals, leading to different sample means. All three are close to 50 (the true mean).' :
                   i === 1 ? 'Sampling variability is the natural variation in sample statistics from sample to sample, even when drawn from the same population. It occurs because each sample contains different individuals selected by chance.' :
                   i === 2 ? 'Larger samples reduce sampling variability. As n increases, sample statistics become closer to population parameters. Standard error decreases as n increases: SE = σ/√n.' :
                   'Solution explaining sampling variability.',
            steps: [
              'Sample statistics vary from sample to sample',
              'Due to random selection of individuals',
              'Larger samples → less variability',
              'Sampling distribution shows all possible sample statistics',
              'Variability measured by standard error'
            ]
          }))
        }
      ]
    },
    {
      id: 'sampling-dist-proportion',
      title: 'Sampling Distribution of Sample Proportion',
      description: 'Distribution of p̂, conditions, calculations',
      skills: [
        {
          id: 'proportion-sampling-dist',
          name: 'Sampling Distribution of p̂',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `pdist-${i + 1}`,
            question: i === 0 ? 'Population proportion p=0.60. Sample size n=100. Find mean and standard deviation of sampling distribution of p̂.' :
                     i === 1 ? 'What conditions must be met for sampling distribution of p̂ to be approximately normal?' :
                     i === 2 ? '40% of voters support candidate. In sample of 200, what is P(p̂ > 0.45)?' :
                     `Calculate properties of sampling distribution for p=${(Math.random() * 0.4 + 0.3).toFixed(2)}, n=${Math.floor(Math.random() * 100) + 50}.`,
            answer: i === 0 ? 'Mean: μ_p̂ = p = 0.60\nStandard Error: σ_p̂ = √[p(1-p)/n] = √[0.60(0.40)/100] = √[0.24/100] = √0.0024 ≈ 0.049\nThe sampling distribution of p̂ is centered at 0.60 with SE of 0.049.' :
                   i === 1 ? 'Conditions for approximately normal:\n1) Random: Random sample or randomized experiment\n2) 10%: n ≤ 10% of population (for independence)\n3) Large Counts: np ≥ 10 and n(1-p) ≥ 10\nIf met, p̂ ~ approximately N(p, √[p(1-p)/n])' :
                   i === 2 ? 'μ_p̂ = 0.40, σ_p̂ = √[0.40(0.60)/200] = √0.0012 ≈ 0.0346\nz = (0.45 - 0.40)/0.0346 ≈ 1.45\nP(Z > 1.45) ≈ 0.0735 or 7.35%\nAbout 7.4% chance sample proportion exceeds 0.45.' :
                   'Solution with sampling distribution calculations.',
            steps: [
              'Mean of p̂: μ_p̂ = p',
              'Standard error of p̂: σ_p̂ = √[p(1-p)/n]',
              'Check conditions: Random, 10%, Large Counts',
              'If conditions met: p̂ ~ N(p, √[p(1-p)/n])',
              'Use normal calculations for probabilities'
            ]
          }))
        }
      ]
    },
    {
      id: 'sampling-dist-mean',
      title: 'Sampling Distribution of Sample Mean',
      description: 'Distribution of x̄, Central Limit Theorem',
      skills: [
        {
          id: 'mean-sampling-dist',
          name: 'Sampling Distribution of x̄ and Central Limit Theorem',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `xbar-${i + 1}`,
            question: i === 0 ? 'Population: μ=50, σ=12. Sample size n=36. Find mean and SE of sampling distribution of x̄.' :
                     i === 1 ? 'State the Central Limit Theorem and explain its importance.' :
                     i === 2 ? 'Population is right-skewed with μ=100, σ=20. Sample size n=64. Is sampling distribution of x̄ approximately normal?' :
                     `Apply CLT for population with μ=${Math.floor(Math.random() * 50) + 50}, σ=${Math.floor(Math.random() * 15) + 10}, n=${Math.floor(Math.random() * 40) + 30}.`,
            answer: i === 0 ? 'Mean: μ_x̄ = μ = 50\nStandard Error: σ_x̄ = σ/√n = 12/√36 = 12/6 = 2\nSampling distribution of x̄ is centered at 50 with SE of 2.' :
                   i === 1 ? 'Central Limit Theorem: When n is sufficiently large, the sampling distribution of x̄ is approximately normal with mean μ and standard deviation σ/√n, regardless of population shape. Importance: Allows us to use normal distribution methods even when population is not normal. Generally n ≥ 30 is sufficient, but depends on how non-normal population is.' :
                   i === 2 ? 'Yes! Despite right-skewed population, with n=64 (≥30) the CLT applies. Sampling distribution of x̄ will be approximately N(100, 20/√64) = N(100, 2.5). Larger samples produce more normal sampling distributions even from skewed populations.' :
                   'Solution applying Central Limit Theorem.',
            steps: [
              'Mean of x̄: μ_x̄ = μ (always)',
              'Standard error of x̄: σ_x̄ = σ/√n',
              'If population is normal: x̄ is exactly normal for any n',
              'If population not normal: x̄ approximately normal for large n (n ≥ 30)',
              'CLT allows normal methods regardless of population shape'
            ]
          }))
        }
      ]
    }
  ]
};

export const unit6: Unit = {
  id: 'unit6',
  title: 'Unit 6: Inference for Categorical Data: Proportions',
  description: 'Confidence intervals and significance tests for proportions',
  weight: '12%–15%',
  topics: [
    {
      id: 'ci-proportion',
      title: 'Confidence Intervals for Proportions',
      description: 'One-sample and two-sample confidence intervals',
      skills: [
        {
          id: 'one-prop-ci',
          name: 'One-Sample Confidence Interval for Proportion',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `ci1p-${i + 1}`,
            question: i === 0 ? 'In sample of 200 adults, 120 support policy. Construct 95% CI for true proportion. Interpret.' :
                     i === 1 ? 'What does "95% confident" mean in context of confidence intervals?' :
                     i === 2 ? 'Sample: n=400, p̂=0.35. Calculate 90% CI for population proportion (z*=1.645).' :
                     `Construct ${['95%', '90%', '99%'][i % 3]} CI for proportion with n=${Math.floor(Math.random() * 200) + 100}.`,
            answer: i === 0 ? 'p̂ = 120/200 = 0.60\nCheck: np̂=120≥10, n(1-p̂)=80≥10 ✓\nSE = √[0.60(0.40)/200] ≈ 0.0346\n95% CI: 0.60 ± 1.96(0.0346) = 0.60 ± 0.068 = (0.532, 0.668)\nInterpretation: We are 95% confident that between 53.2% and 66.8% of all adults support the policy.' :
                   i === 1 ? '"95% confident" means: If we repeated this sampling process many times and constructed a CI each time, about 95% of those intervals would contain the true population parameter. It does NOT mean there\'s a 95% chance the true parameter is in this specific interval.' :
                   i === 2 ? 'p̂ = 0.35, n = 400\nSE = √[0.35(0.65)/400] = √0.0005688 ≈ 0.0238\n90% CI: 0.35 ± 1.645(0.0238) = 0.35 ± 0.039 = (0.311, 0.389)' :
                   'Solution with confidence interval calculation and interpretation.',
            steps: [
              'Check conditions: Random, 10%, Large Counts (np̂≥10, n(1-p̂)≥10)',
              'Calculate p̂ = x/n',
              'Calculate SE = √[p̂(1-p̂)/n]',
              'Find critical value z* for confidence level',
              'CI: p̂ ± z*·SE',
              'Interpret in context'
            ]
          }))
        },
        {
          id: 'two-prop-ci',
          name: 'Two-Sample Confidence Interval for Difference in Proportions',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `ci2p-${i + 1}`,
            question: i === 0 ? 'Group A: 80/200 success. Group B: 60/150 success. Construct 95% CI for p₁ - p₂.' :
                     i === 1 ? 'A CI for p₁ - p₂ is (-0.08, 0.12). What can we conclude about the difference?' :
                     i === 2 ? 'Treatment: 45/100 improved. Control: 30/100 improved. Is there evidence treatment is better? Use 95% CI.' :
                     `Construct CI for difference in proportions with n₁=${Math.floor(Math.random() * 100) + 50}, n₂=${Math.floor(Math.random() * 100) + 50}.`,
            answer: i === 0 ? 'p̂₁ = 80/200 = 0.40, p̂₂ = 60/150 = 0.40\nSE = √[0.40(0.60)/200 + 0.40(0.60)/150] = √[0.0012 + 0.0016] = √0.0028 ≈ 0.0529\n95% CI: (0.40-0.40) ± 1.96(0.0529) = 0 ± 0.104 = (-0.104, 0.104)\nWe\'re 95% confident the true difference is between -10.4% and +10.4%. Since 0 is in interval, no significant difference.' :
                   i === 1 ? 'Since 0 is contained in the interval (-0.08, 0.12), we cannot conclude there is a significant difference between p₁ and p₂. The data are consistent with p₁ = p₂.' :
                   i === 2 ? 'p̂₁=0.45, p̂₂=0.30\nSE = √[0.45(0.55)/100 + 0.30(0.70)/100] = √[0.002475 + 0.0021] ≈ 0.0673\n95% CI: 0.15 ± 1.96(0.0673) = 0.15 ± 0.132 = (0.018, 0.282)\nSince interval is entirely above 0, we have evidence treatment is better. We\'re 95% confident treatment improves success rate by 1.8% to 28.2%.' :
                   'Solution with two-sample CI and interpretation.',
            steps: [
              'Check conditions for both groups',
              'Calculate p̂₁ and p̂₂',
              'Calculate SE = √[p̂₁(1-p̂₁)/n₁ + p̂₂(1-p̂₂)/n₂]',
              'CI: (p̂₁ - p̂₂) ± z*·SE',
              'If 0 in interval: no significant difference',
              'If 0 not in interval: significant difference'
            ]
          }))
        }
      ]
    },
    {
      id: 'test-proportion',
      title: 'Significance Tests for Proportions',
      description: 'One-sample and two-sample hypothesis tests',
      skills: [
        {
          id: 'one-prop-test',
          name: 'One-Sample z-Test for Proportion',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `test1p-${i + 1}`,
            question: i === 0 ? 'Company claims 30% of customers prefer Product A. Sample of 200: 72 prefer A. Test at α=0.05 if true proportion differs from 30%.' :
                     i === 1 ? 'Explain the difference between Type I and Type II errors.' :
                     i === 2 ? 'H₀: p=0.50 vs Hₐ: p>0.50. Sample: n=100, p̂=0.58. Calculate test statistic and P-value.' :
                     `Perform hypothesis test for proportion with H₀: p=${(Math.random() * 0.3 + 0.4).toFixed(2)}.`,
            answer: i === 0 ? 'H₀: p = 0.30 vs Hₐ: p ≠ 0.30 (two-tailed)\np̂ = 72/200 = 0.36\nCheck: 200(0.30)=60≥10, 200(0.70)=140≥10 ✓\nSE₀ = √[0.30(0.70)/200] ≈ 0.0324\nz = (0.36-0.30)/0.0324 ≈ 1.85\nP-value = 2·P(Z > 1.85) ≈ 2(0.0322) = 0.0644\nSince P=0.0644 > α=0.05, fail to reject H₀. Insufficient evidence that true proportion differs from 30%.' :
                   i === 1 ? 'Type I Error: Rejecting H₀ when it\'s actually true (false positive). Probability = α (significance level).\nType II Error: Failing to reject H₀ when it\'s false (false negative). Probability = β.\nExample: Type I = convicting innocent person. Type II = acquitting guilty person.' :
                   i === 2 ? 'SE₀ = √[0.50(0.50)/100] = √0.0025 = 0.05\nz = (0.58-0.50)/0.05 = 0.08/0.05 = 1.60\nP-value = P(Z > 1.60) ≈ 0.0548\nModerate evidence against H₀ (marginally significant at α=0.10).' :
                   'Solution with complete hypothesis test.',
            steps: [
              'State hypotheses: H₀: p = p₀ vs Hₐ',
              'Check conditions using p₀',
              'Calculate test statistic: z = (p̂ - p₀) / SE₀ where SE₀ = √[p₀(1-p₀)/n]',
              'Calculate P-value from z',
              'Make decision: reject H₀ if P-value < α',
              'State conclusion in context'
            ]
          }))
        },
        {
          id: 'two-prop-test',
          name: 'Two-Sample z-Test for Difference in Proportions',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `test2p-${i + 1}`,
            question: i === 0 ? 'Test if treatment proportion differs from control. Treatment: 75/150 success. Control: 60/150 success. α=0.05.' :
                     i === 1 ? 'Why do we pool proportions when testing H₀: p₁ = p₂ but not when making CIs?' :
                     i === 2 ? 'Drug A: 80/200 cured. Drug B: 65/200 cured. Test if Drug A better (one-tailed) at α=0.05.' :
                     `Perform two-sample test with n₁=${Math.floor(Math.random() * 100) + 100}, n₂=${Math.floor(Math.random() * 100) + 100}.`,
            answer: i === 0 ? 'H₀: p₁ = p₂ vs Hₐ: p₁ ≠ p₂\np̂₁=75/150=0.50, p̂₂=60/150=0.40\np̂_pooled = (75+60)/(150+150) = 135/300 = 0.45\nSE_pooled = √[0.45(0.55)(1/150 + 1/150)] = √[0.2475(0.0133)] ≈ 0.0575\nz = (0.50-0.40)/0.0575 ≈ 1.74\nP-value = 2·P(Z > 1.74) ≈ 2(0.041) = 0.082\nP=0.082 > 0.05, fail to reject. Insufficient evidence proportions differ.' :
                   i === 1 ? 'For testing: Under H₀, we assume p₁=p₂, so we pool data to get best estimate of common proportion. This gives more accurate SE under null.\nFor CI: We\'re estimating difference, not assuming equality, so we use separate estimates for each proportion.' :
                   i === 2 ? 'H₀: p₁ = p₂ vs Hₐ: p₁ > p₂\np̂₁=0.40, p̂₂=0.325\np̂_pooled = (80+65)/400 = 0.3625\nSE = √[0.3625(0.6375)(1/200+1/200)] ≈ 0.048\nz = (0.40-0.325)/0.048 ≈ 1.56\nP-value = P(Z>1.56) ≈ 0.059\nP=0.059 > 0.05, fail to reject. Weak evidence Drug A is better.' :
                   'Solution with two-sample hypothesis test.',
            steps: [
              'State hypotheses: H₀: p₁ = p₂',
              'Calculate pooled proportion: p̂_c = (x₁+x₂)/(n₁+n₂)',
              'SE_pooled = √[p̂_c(1-p̂_c)(1/n₁ + 1/n₂)]',
              'z = (p̂₁ - p̂₂) / SE_pooled',
              'Calculate P-value',
              'Conclude in context'
            ]
          }))
        }
      ]
    }
  ]
};